import { addDays, format, isToday, isTomorrow } from 'date-fns';
import { ISelectOptions } from '../components/dropdown';
import { getDateDescription } from './get-date-description';
import { safelyConsoleError } from './safely-console-error';

type DateLabelSuffix = '(Today)' | '(Tomorrow)';

const defaultAppendageCallback = (date: Date): DateLabelSuffix | '' => {
	if (isToday(date)) return '(Today)';
	if (isTomorrow(date)) return '(Tomorrow)';
	return '';
};

/**
 * Creates a list of days for a dropdown selection, with a custom label suffix, subject to the provided callback.
 * @param length - The number of days to include in the list - they will appear sequentially from the start date.
 * @param startDate - The date to start the list from (default is today).
 * @param appendageCallback - A callback function to determine the label suffix for each date - defaults to a '(Today)'/'(Tomorrow)' resolver.
 * @returns An array of ISelectOptions representing the upcoming days.
 * @example
 * const upcomingDays = createDayDropdownOptions(7); // assume today is 1st October 2023
 * // [
 * //   { value: '2023-10-01', label: 'Sunday, 1st October (Today)' },
 * //   { value: '2023-10-02', label: 'Monday, 2nd October (Tomorrow)' },
 * //   ...
 * // ]
 */
export const createDescriptiveDateDropdownOptions = (
	length: number,
	startDate = new Date(),
	appendageCallback: (date: Date, index: number) => DateLabelSuffix | '' = defaultAppendageCallback, // allows for custom suffixes
): ISelectOptions[] => {
	if (length <= 0 || !length) return [];
	return Array.from({ length }, (_, i) => {
		// To be implemented on the {value}
		const date = addDays(startDate, i);
		let suffix: DateLabelSuffix | '' = '';
		// To be implemented on the {label} - we'll end up with e.g. 'Sunday, 1st October <suffix-return>'
		if (appendageCallback)
			try {
				suffix = appendageCallback(date, i);
			} catch (error) {
				safelyConsoleError('Error occurred in appendageCallback:', error);
				// suffix remains ''
			}

		return {
			value: `${format(date, 'yyyy')}-${format(date, 'MM')}-${format(date, 'dd')}`,
			label: getDateDescription(date, suffix),
		};
	});
};
